<template>
	<div
		v-if="ready"
		class="container"
	>

		<div class="row center">
			<div class="col-lg-8 col-md-10 col-sm-12">
				<h1 class="main-content-title">
					{{ translations.navigation.languages }}
					<div v-if="selectableLanguages.length" class="flex-start">
						<el-button
							@click="openAddDialog()"
							type="primary"
							plain
						>
							{{ translations.labels.addNew }}
						</el-button>
					</div>
				</h1>
			</div>
		</div>

		<div class="row center">
			<div class="col-lg-8 col-md-10 col-sm-12">
				<div class="box">

					<div class="box-head with-line">
						<div class="box-head-text">
							{{ translations.labels.languagesText }}
						</div>
					</div>

					<div class="box-body">
						<el-table
							v-if="languages.length"
							:data="languages"
							style="width: 100%"
						>
							<el-table-column width="40">
								<template slot-scope="scope">
									<img
										:src="`/images/flags/${scope.row.code}.png`"
										:class="{ 'opacity-50': !scope.row.enabled }"
									/>
								</template>
							</el-table-column>
							<el-table-column prop="title" :label="translations.labels.title">
								<template slot-scope="scope">
									<span :class="{ 'color-light': !scope.row.enabled }">
										{{ scope.row.title }}
									</span>
								</template>
							</el-table-column>
							<el-table-column align="right" width="100">
								<template slot-scope="scope">
									<div class="align-right">
										<el-dropdown
											@command="handleCommand"
											size="medium"
											placement="bottom-end"
										>
											<el-button plain size="mini">
												<span class="el-icon-s-tools" />
											</el-button>
											<el-dropdown-menu slot="dropdown">
												<el-dropdown-item :command="{ action: 'upload', id: scope.row.code }">
													{{ translations.labels.uploadCsv }}
												</el-dropdown-item>
												<el-dropdown-item :command="{ action: 'download', id: scope.row.code }">
													{{ translations.labels.downloadCsv }}
												</el-dropdown-item>
												<el-dropdown-item :command="{ action: 'edit', id: scope.row }">
													{{ scope.row.enabled ? translations.labels.deactivate : translations.labels.activate }}
												</el-dropdown-item>
												<el-dropdown-item :command="{ action: 'delete', id: scope.row.code }">
													{{ translations.labels.delete }}
												</el-dropdown-item>
											</el-dropdown-menu>
										</el-dropdown>
									</div>
								</template>
							</el-table-column>
						</el-table>
					</div>

				</div>
			</div>
		</div>

		<!-- delete dialog -->
		<DeleteConfirmation
			:show="dialog.delete"
			:processing="processing.delete"
			:message="translations.messages.warningRemoveLanguage"
			:label="translations.labels.deleteLanguage"
			confirmAction="proceed-delete"
			cancelAction="cancel-delete"
		/>
		<!-- /delete dialog -->

		<!-- add language dialog -->
		<el-dialog
			:title="translations.labels.addLanguage"
			@closed="closedAddDialog"
			:visible.sync="dialog.add"
			:show-close="false"
			:close-on-press-escape="false"
			:close-on-click-modal="false"
			width="500px"
		>
			{{ translations.messages.warningAddLanguage }}
			<hr />
			<el-form
				:model="form"
				ref="form"
			>
				<div class="row">
					<div class="col-lg-3 col-sm-12">
						<label class="required">
							{{ translations.labels.language }}
						</label>
					</div>
					<div class="col-lg-8 col-sm-12">
						<el-form-item prop="code">
							<el-select
								v-model="form.code"
								:placeholder="translations.labels.selectLanguage"
								style="width: 100%"
							>
								<el-option
									v-for="(language, index) in selectableLanguages"
									:key="index"
									:label="language.title"
									:value="language.code"
								>
									<div class="flex-start" style="height: 34px;">
										<span>
											<img
												:src="`/images/flags/${language.code}.png`"
												class="el-select-dropdown__item-flag"
											/>
										</span>
										<span>
											{{ language.title }}
										</span>
									</div>
								</el-option>
							</el-select>
						</el-form-item>
					</div>
				</div>
				<div class="row">
					<div class="col-lg-12 align-right">
						<el-form-item class="no-margin-bottom">
							<el-button
								@click="submitForm('form')"
								:loading="processing.add"
								:disabled="!form.code"
								:type="!form.code ? 'info' : 'primary'"
								native-type="submit"
								plain
							>
								{{ translations.labels.addLanguage }}
							</el-button>
							<el-button
								@click="closeAddDialog()"
								plain
							>
								{{ translations.labels.cancel }}
							</el-button>
						</el-form-item>
					</div>
				</div>
			</el-form>
		</el-dialog>
		<!-- /add language dialog -->

		<!-- upload dialog -->
		<el-dialog
			:title="translations.labels.uploadCsv"
			@closed="closedUploadDialog"
			:visible.sync="dialog.upload"
			:show-close="false"
			:close-on-press-escape="false"
			:close-on-click-modal="false"
			width="400px"
		>
			<div class="margin-bottom-20">
				{{ translations.messages.warningUploadCsv }}
			</div>
			<el-upload
				:action="`${$config.api}/translations/csv/${tempData}`"
				:headers="headers"
				:before-upload="beforeUploadCsv"
				:on-change="uploadCsvChanged"
				:on-success="uploadCsvSuccess"
				:auto-upload="false"
				:multiple="false"
				:limit="1"
				ref="upload"
				accept=".csv"
				drag
			>
				<i class="el-icon-upload"></i>
				<div class="el-upload__text">
					{{ translations.labels.dropFile }}
				</div>
			</el-upload>
			<div slot="footer" class="dialog-footer">
				<el-button
					@click="uploadCsv()"
					:loading="processing.upload"
					:type="!uploaded ? 'info' : 'primary'"
					:disabled="!uploaded"
					plain
				>
					{{ translations.labels.upload }}
				</el-button>
				<el-button
					@click="closeUploadDialog()"
					plain
				>
					{{ translations.labels.cancel }}
				</el-button>
			</div>
		</el-dialog>
		<!-- /upload dialog -->

		<!-- edit dialog -->
		<el-dialog
			@closed="closedEditDialog"
			:title="translations.labels.areYouSure"
			:visible.sync="dialog.edit"
			:show-close="false"
			:close-on-press-escape="false"
			:close-on-click-modal="false"
			width="400px"
		>
			{{ form.enabled ? translations.messages.warningDeactivateLanguage : translations.messages.warningActivateLanguage }}
			<div slot="footer" class="dialog-footer">
				<el-button
					@click="ajaxUpdateLanguage()"
					:loading="processing.edit"
					:type="form.enabled ? 'danger' : 'primary'"
					plain
				>
					{{ form.enabled ? translations.labels.deactivate : translations.labels.activate }}
				</el-button>
				<el-button
					@click="closeEditDialog()"
					plain
				>
					{{ translations.labels.cancel }}
				</el-button>
			</div>
		</el-dialog>
		<!-- /edit dialog -->

	</div>
</template>

<script>
import { mapState } from 'vuex'
import {
	_getLanguages,
	_addLanguage,
	_updateLanguage,
	_deleteLanguage
} from '@/services/api/settings'
import { _downloadFile } from '@/services/api/translations'
import { EventBus } from '@/services/event-bus'

import loadTranslations from '@/services/mixins/loadTranslations'
import sortArray from '@/services/mixins/sortArray'

export default {
	name: 'Languages',
	metaInfo () {
		return {
			title: this.translations.navigation.languages
		}
	},
	mixins: [
		loadTranslations,
		sortArray
	],
	components: {
		DeleteConfirmation: () => import( /* webpackPrefetch: true */ '@/views/Private/Components/Dialog/Delete')
	},
	data () {
		return {
			confirm: '',
			dialog: {
				delete: false,
				add: false,
				edit: false,
				upload: false
			},
			form: {},
			headers: {
				Application: this.$store.state.axiosToken,
				Authorization: `Bearer ${this.$store.state.auth.access_token}`
			},
			languages: [],
			processing: {
				delete: false,
				add: false,
				edit: false,
				upload: false,
				download: false
			},
			selectableLanguages: [],
			tempData: null,
			uploaded: false
		}
	},
	computed: {
		...mapState(['availableLanguages'])
	},
	created () {
		this.ajaxLoadLanguages()
	},
	mounted () {
		EventBus.$on('cancel-delete', () => this.closeDeleteDialog())
		EventBus.$on('proceed-delete', () => this.ajaxDeleteLanguage())
	},
	destroyed () {
		EventBus.$off('cancel-delete')
		EventBus.$off('proceed-delete')
    },
	methods: {

		ajaxLoadLanguages () {
			_getLanguages()
				.then(response => {
					this.languages = response
					this.findSelectableLanguages()
					this.removeLoader()
				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
					}, 200)
				})
		},
		ajaxAddLanguage () {
			this.processing.add = true
			const language = this.languages.find(item => item.code === this.form.code)
			const data = {
				code: this.form.code,
				title: language.title,
				enabled: false
			}
			_addLanguage(data)
				.then(() => {
					this.displayMessage(this.translations.messages.successUploadLanguage, 'success')
					this.ajaxLoadLanguages()
					setTimeout (() => {
						this.closeAddDialog()
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.processing.add = false
						this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
					}, 200)
				})
		},
		ajaxUpdateLanguage () {
			this.processing.edit = true
			const data = {
				code: this.form.code,
				enabled: !this.form.enabled
			}
			_updateLanguage(data)
			.then(() => {
				const index = this.languages.findIndex(item => item.code === this.form.code)
				this.languages[index].enabled = !this.form.enabled
				this.findAvailableLanguages()
				setTimeout (() => {
					this.closeEditDialog()
					this.displayMessage(this.languages[index].enabled ? this.translations.messages.successActivateLanguage : this.translations.messages.successDeactivateLanguage, 'success')
				}, 200)
			})
			.catch(error => {
				const errorCode = error.response.data.status
				setTimeout (() => {
					this.closeEditDialog()
					this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
				}, 200)
			})
		},
		ajaxDeleteLanguage () {
			this.processing.delete = true
			_deleteLanguage(this.tempData)
			.then(() => {
				const index = this.languages.findIndex(item => item.code === this.tempData)
				this.languages.splice(index, 1)
				this.findSelectableLanguages()
				this.findAvailableLanguages()
				setTimeout (() => {
					this.displayMessage(this.translations.messages.successDeleteLanguage, 'success')
					this.closeDeleteDialog()
				}, 200)
			})
			.catch(error => {
				const errorCode = error.response.data.status
				setTimeout (() => {
					this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
					this.processing.delete = false
				}, 200)
			})
		},
		ajaxDownloadCSV (code) {
			this.tempData = code
			this.processing.download = true
			_downloadFile(code)
				.then(response => {
					const url = window.URL.createObjectURL(new Blob([response]))
					const link = document.createElement('a')
					link.href = url
					link.setAttribute('download', `translation-${code}.csv`)
					document.body.appendChild(link)
					link.click()
					setTimeout (() => {
						this.displayMessage(this.translations.messages.successDownloadFile, 'success')
						this.tempData = null
						this.processing.download = false
					}, 200)
				})
				.catch(error => {
					const errorCode = error.response.data.status
					setTimeout (() => {
						this.displayMessage(this.translations.statusCodes[errorCode] ? this.translations.statusCodes[errorCode] : this.translations.statusCodes['default'], 'error')
						this.processing.download = false
					}, 200)
				})
		},

		submitForm (form) {
			this.$refs[form].validate((valid) => {
				if (valid) {
					this.ajaxAddLanguage()
				}
			})
		},
		findAvailableLanguages () {
			const temp = this.languages.filter(item => item.enabled === true)
			this.$store.dispatch('setAvailableLanguages', this.sortArray(temp, 'title', 'asc'))
		},
		findSelectableLanguages () {
			this.selectableLanguages = this.languages.filter(item => {
				const temp = this.languages.findIndex(i => i.code === item.code)
				return temp < 0
			})
		},
		openAddDialog () {
			this.dialog.add = true
			this.form = {
				code: null
			}
			this.$nextTick(() => {
				this.$refs.form.clearValidate()
			})
		},
		closeAddDialog () {
			this.dialog.add = false
			this.processing.add = false
		},
		closedAddDialog () {
			this.form = {}
		},
		openDeleteDialog (id) {
			this.dialog.delete = true
			this.tempData = id
		},
		closeDeleteDialog () {
			this.dialog.delete = false
			this.processing.delete = false
			this.tempData = null
		},
		openEditDialog (item) {
			this.dialog.edit = true
			this.form = item
		},
		closeEditDialog () {
			this.dialog.edit = false
			this.processing.edit = false
		},
		closedEditDialog () {
			this.form = {}
		},
		openUploadDialog (code) {
			this.dialog.upload = true
			this.tempData = code
		},
		closeUploadDialog () {
			this.dialog.upload = false
			this.processing.upload = false
		},
		closedUploadDialog () {
			this.$refs.upload.clearFiles()
			this.tempData = null
		},
		beforeUploadCsv () {
			this.processing.upload = true
		},
		uploadCsvChanged () {
			this.uploaded = true
		},
		uploadCsv () {
			this.$refs.upload.submit()
		},
		uploadCsvSuccess () {
			setTimeout (() => {
				this.displayMessage(this.translations.messages.successUploadFile, 'success')
				this.processing.upload = false
				this.uploaded = false
				this.closeUploadDialog()
			}, 200)
		},
		handleCommand (el) {
			if (el.action === 'edit') {
				this.openEditDialog(el.id)
			}
			if (el.action === 'download') {
				this.ajaxDownloadCSV(el.id)
			}
			if (el.action === 'upload') {
				this.openUploadDialog(el.id)
			}
			if (el.action === 'delete') {
				this.openDeleteDialog(el.id)
			}
		}

	}
}
</script>
